import React, { useState, useEffect } from "react";
import { DatePicker } from "antd";
import moment, { Moment } from "moment";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

interface PredefinedDateRangesProps {
  onDateChange: (start: Moment, end: Moment) => void;
  startDate?: Moment | null;
  endDate?: Moment | null;
  dateFormat?: string;
}

const PredefinedDateRanges: React.FC<PredefinedDateRangesProps> = ({
  onDateChange,
  startDate,
  endDate,
  dateFormat = "DD/MM/YYYY",
}) => {
  const [dateRange, setDateRange] = useState<[Moment, Moment]>([
    moment(),
    moment(),
  ]);

  useEffect(() => {
    if (startDate && endDate) {
      setDateRange([startDate, endDate]);
    }
  }, [startDate, endDate]);

  // Moment → Dayjs
  const momentToDayjs = (momentDate: Moment) => dayjs(momentDate.toDate());

  // Dayjs → Moment
  const dayjsToMoment = (dayjsDate: any): Moment | null =>
    dayjsDate ? moment(dayjsDate.toDate()) : null;

  const handleDateChange = (dates: [any, any] | null) => {
    if (dates && dates[0] && dates[1]) {
      const start = dayjsToMoment(dates[0])!.clone().startOf("day");
      const end = dayjsToMoment(dates[1])!.clone().endOf("day");

      setDateRange([start, end]);
      onDateChange(start, end);
    }
  };

  const getAntdValue = (): [any, any] => {
    return [momentToDayjs(dateRange[0]), momentToDayjs(dateRange[1])];
  };

  const now = moment();

  const ranges: Record<string, [any, any]> = {
    Today: [momentToDayjs(now), momentToDayjs(now)],

    Yesterday: [
      momentToDayjs(now.clone().subtract(1, "day")),
      momentToDayjs(now.clone().subtract(1, "day")),
    ],

    "Last 7 Days": [
      momentToDayjs(now.clone().subtract(6, "days")),
      momentToDayjs(now),
    ],

    "Last 30 Days": [
      momentToDayjs(now.clone().subtract(29, "days")),
      momentToDayjs(now),
    ],
  };

  // Disable dates older than last 3 months & future dates
  const disabledDate = (current: any) => {
    if (!current) return false;

    const threeMonthsAgo = moment().subtract(3, "month").startOf("day");

    const today = moment().endOf("day");

    return (
      current.toDate() < threeMonthsAgo.toDate() ||
      current.toDate() > today.toDate()
    );
  };

  const formatLabel = () => {
    return `${dateRange[0].format(dateFormat)} - ${dateRange[1].format(
      dateFormat
    )}`;
  };

  return (
    <div style={{ width: "100%" }}>
      <RangePicker
        onChange={handleDateChange}
        value={getAntdValue()}
        style={{ width: "100%" }}
        ranges={ranges}
        format={dateFormat}
        allowClear={false}
        disabledDate={disabledDate}
        renderExtraFooter={() => (
          <div
            style={{
              padding: "10px",
              textAlign: "center",
              borderTop: "1px solid #f0f0f0",
              fontSize: "12px",
              color: "#666",
            }}
          >
            {formatLabel()}
          </div>
        )}
      />
    </div>
  );
};

export default PredefinedDateRanges;
